import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    EnvironmentInjector,
    inject,
    input,
    InputSignal,
    OnChanges,
    OnInit,
    runInInjectionContext,
    Signal,
    signal,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { exportIconFromRegistry } from './exportIconFromRegistry';
import { IconService } from './ce-icon.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { SafeHtml } from '@angular/platform-browser';
import { Icons } from './ICONLIB';

type IconSizes = IconSizeDefinition | null;
/**
 * Defines the possible icon size definitions for the CeIconComponent.
 */
type IconSizeDefinition = 'ce-icon-xs' | 'ce-icon-sm' | 'ce-icon-md' | 'ce-icon-lg';

/**
 * CeIconComponent renders an SVG icon based on provided data.
 *
 * This component supports dynamic sizing and inversion of icons based on attributes,
 * and leverages the `getIcon` function to retrieve SVG content based on a given name.
 */
@Component({
    selector: 'ce-icon',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './ce-icon.component.html',
    styleUrl: './ce-icon.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class]': '_computedClass()',
    },
})
export class CeIconComponent implements OnInit, OnChanges {
    /**
     * Input property for the SVG icon name or path.
     * Required input for the component.
     */

    svgIcon: InputSignal<keyof Icons> = input.required<keyof Icons>();

    size: InputSignal<IconSizes> = input<IconSizes>(null);

    /** Holds the resolved icon name after fetching from the `getIcon` function. */

    injector = inject(EnvironmentInjector);

    safeIcon!: Signal<SafeHtml | undefined>;

    /** Predefined list of supported icon sizes for the component. */
    iconDefinedSizes: IconSizeDefinition[] = ['ce-icon-xs', 'ce-icon-sm', 'ce-icon-md', 'ce-icon-lg'];

    /** Selected icon size CSS class based on detected attributes or default. */
    selectedIconSize = 'CeIcon CeIcon--ce-icon-sm';

    /** Signal to determine if the icon should be inverted. */
    protected readonly _isInverse = signal<string>('');

    /** Signal to determine if the icon should be disabled. */
    protected readonly _isDisabled = signal<string>('');

    /** Signal representing the currently selected icon size CSS class. */
    protected readonly _selectedIconSize = signal<string>('CeIcon CeIcon--ce-icon-sm');

    /** Injects the ElementRef for accessing the component's native element. */
    readonly #elem = inject(ElementRef);
    readonly #iconService = inject(IconService);

    /**
     * Computed property to dynamically generate the CSS classes based on current states.
     * Combines the inversion and selected icon size signals into a single class string.
     */
    protected readonly _computedClass = computed(() => {
        return `${this._isInverse()} ${this._selectedIconSize()} ${this._isDisabled()}`;
    });

    /** Lifecycle hook executed when component initializes. */
    ngOnInit(): void {
        // Sets the initial icon size based on attributes.
        this.#setIconSize();

        // Checks for inversion attribute and applies the corresponding class.
        this.#setIconInverse();

        // Checks for disabled attribute and applies the corresponding class.
        this.#setIconDisabled();
    }

    /** Lifecycle hook executed when component changes. */
    ngOnChanges(changes: SimpleChanges): void {
        // Fetches the icon name from the provided svgIcon using getIcon function.
        runInInjectionContext(this.injector, () => {
            this.safeIcon = toSignal(this.#iconService.getIcon(changes['svgIcon'].currentValue, exportIconFromRegistry(changes['svgIcon'].currentValue)));
        });
    }

    /**
     * Sets the selected icon size based on attributes defined in `iconDefinedSizes`.
     * Retrieves the native element and checks for attributes matching predefined icon sizes,
     * then updates `_selectedIconSize`.
     */
    #setIconSize() {
        const inputIconSize = this.size();
        if (inputIconSize !== null) {
            this._selectedIconSize.set('CeIcon CeIcon--' + inputIconSize.toLowerCase());
        } else {
            // set using attributes
            const iconEle = this.#elem.nativeElement as HTMLElement;

            for (const iconSize of this.iconDefinedSizes) {
                if (iconEle.hasAttribute(iconSize)) {
                    this._selectedIconSize.set('CeIcon CeIcon--' + iconSize.toLowerCase());
                    break;
                }
            }
        }
    }

    /**
     * Checks for the presence of 'ce-icon-inverse' attribute on the native element.
     * If present, sets the `_isInverse` signal to apply the inversion CSS class.
     */
    #setIconInverse() {
        const iconEle = this.#elem.nativeElement as HTMLElement;
        if (iconEle.hasAttribute('ce-icon-inverse')) {
            this._isInverse.set('CeIcon--ce-icon-inverse');
        }
    }

    #setIconDisabled() {
        const iconEle = this.#elem.nativeElement as HTMLElement;
        if (iconEle.hasAttribute('ce-icon-disabled')) {
            this._isDisabled.set('CeIcon--ce-icon-disabled');
        }
    }
}
